import { QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '../../../shared/types/api-client'
import { FunctionApp, FunctionDetail } from '../types/FunctionApp'
import { ExecutionResultDto } from '../types/ExecuteFunctionDto'
import { dashboardKeys } from '@src/shell/api/dashboard-api'

const SCOPE = 'functionApps'
const FUNCTIONS_SCOPE = 'functions'
const LIST = 'list'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  list: () => [{ scope: SCOPE, entity: LIST }] as const,
  functions: (appId: string) => [{ scope: FUNCTIONS_SCOPE, entity: LIST, appId }] as const,
}

const getFunctionApps = async () => {
  const apiClient = getApiClient()
  const response = await apiClient.get('FunctionApps')
  return response.data as FunctionApp[]
}

export function useFunctionApps(): [FunctionApp[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.list()],
    queryFn: getFunctionApps,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

export function useRefreshFunctionApps() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      const apiClient = getApiClient()
      const response = await apiClient.post('FunctionApps/Refresh')
      return response.data as FunctionApp[]
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.all() })
    },
  })
  return [mutateAsync, isPending] as const
}

const getFunctions = async ({
  queryKey: [{ appId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['functions']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get(`FunctionApps/${encodeURIComponent(appId)}/Functions`)
  return response.data as FunctionDetail[]
}

export function useFunctions(appId: string | null): [FunctionDetail[], boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.functions(appId ?? '')],
    queryFn: getFunctions,
    placeholderData: [],
    enabled: !!appId,
  })
  return [data ?? [], isFetching]
}

interface SetTimerEnabledParams {
  appId: string
  functionName: string
  enabled: boolean
  reason?: string
}

export function useSetTimerEnabled() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ appId, functionName, enabled, reason }: SetTimerEnabledParams) => {
      const apiClient = getApiClient()
      const response = await apiClient.post(
        `FunctionApps/${encodeURIComponent(appId)}/TimerFunctions/${encodeURIComponent(functionName)}/SetEnabled`,
        { enabled, reason },
      )
      return response.data as ExecutionResultDto
    },
    onSuccess: (_data, { appId, functionName, enabled }) => {
      queryClient.setQueryData<FunctionApp[]>([...keysFactory.list()], (apps) =>
        apps?.map((app) =>
          app.id === appId
            ? {
                ...app,
                timerTriggers: app.timerTriggers.map((timer) =>
                  timer.name === functionName ? { ...timer, isDisabled: !enabled } : timer,
                ),
              }
            : app,
        ),
      )
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.commandSummary() })
    },
  })
  return [mutateAsync, isPending] as const
}

export function useRefreshFunctions() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (appId: string) => {
      const apiClient = getApiClient()
      const response = await apiClient.post(`FunctionApps/${encodeURIComponent(appId)}/Functions/Refresh`)
      return response.data as FunctionDetail[]
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [{ scope: FUNCTIONS_SCOPE }] })
    },
  })
  return [mutateAsync, isPending] as const
}
